"use client";

import { useContext, useMemo, type ReactNode } from "react";
import {
  AppRouterContext,
  type AppRouterInstance,
  type NavigateOptions,
} from "next/dist/shared/lib/app-router-context.shared-runtime";
import { canNavigateAway, notifyNavigation } from "@/lib/navigationGuard";

/**
 * Routes every `useRouter()` navigation in the app through the guard registry.
 *
 * `useRouter()` copies the router's methods into a memo private to each
 * caller, so a component can only ever patch its own copy — replacing the
 * value of the context it reads is the one place that reaches every caller.
 * That keeps guarded pages safe from navigation call sites they know nothing
 * about, including ones added later.
 *
 * `AppRouterContext` is not part of Next's public API. A release that moves
 * it fails the build; one that keeps it but stops reading it fails
 * `cypress/component/navigationGuardProvider.cy.tsx`.
 *
 * `back` and `forward` are left alone — they only call `history.back`/
 * `forward`, which `useNavigationGuard` already covers via `popstate`. So is
 * `refresh`, which reloads the route rather than leaving it.
 */
export const NavigationGuardProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const router = useContext(AppRouterContext);

  const guardedRouter = useMemo<AppRouterInstance | null>(() => {
    if (!router) return null;

    return {
      ...router,
      push: (href: string, options?: NavigateOptions) => {
        if (!canNavigateAway(href)) return;
        notifyNavigation(href);
        router.push(href, options);
      },
      replace: (href: string, options?: NavigateOptions) => {
        if (!canNavigateAway(href)) return;
        notifyNavigation(href);
        router.replace(href, options);
      },
    };
  }, [router]);

  // Server render, where there is no router to guard.
  if (!guardedRouter) return <>{children}</>;

  return (
    <AppRouterContext.Provider value={guardedRouter}>
      {children}
    </AppRouterContext.Provider>
  );
};
